import { useState } from 'react';
import { Product, ProductCreate, ProductUpdate } from '../types/costwand.d';
import { useProduct } from '../hooks/useProduct.ts';
import ProductForm from '../components/ProductForm.tsx';
import ProductTable from '../components/ProductTable.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '../components/ui/dialog.tsx';

// Import new lookup hooks
import { useVendorLookup } from '../hooks/useVendorLookup.ts';
import { useFamilyLookup } from '../hooks/useFamilyLookup.ts';
import { useSubFamilyLookup } from '../hooks/useSubFamilyLookup.ts';
import { useBarcodeType1Lookup } from '../hooks/useBarcodeType1Lookup.ts';
import { useBarcodeType2Lookup } from '../hooks/useBarcodeType2Lookup.ts';
import { useCurrencyLookup } from '../hooks/useCurrencyLookup.ts';
import { useUnitOfMeasureLookup } from '../hooks/useUnitOfMeasureLookup.ts';
import { Spinner } from '../components/ui/spinner.tsx'; // Import Spinner
import { useNavigate } from 'react-router-dom'; // Import useNavigate

const ProductsPage = () => {
  const {
    products,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    createProduct,
    updateProduct,
    deleteProduct,
  } = useProduct();

  const navigate = useNavigate(); // Initialize useNavigate

  // Initialize all lookup hooks
  const { vendors, loading: vendorsLoading, error: vendorsError } = useVendorLookup();
  const { families, loading: familiesLoading, error: familiesError } = useFamilyLookup();
  const { subFamilies, loading: subFamiliesLoading, error: subFamiliesError } = useSubFamilyLookup();
  const { barcodeType1s, loading: barcodeType1sLoading, error: barcodeType1sError } = useBarcodeType1Lookup();
  const { barcodeType2s, loading: barcodeType2sLoading, error: barcodeType2sError } = useBarcodeType2Lookup();
  const { currencies, loading: currenciesLoading, error: currenciesError } = useCurrencyLookup();
  const { unitsOfMeasure, loading: unitsOfMeasureLoading, error: unitsOfMeasureError } = useUnitOfMeasureLookup();

  // Combine all lookup loading states
  const allLookupsLoading =
    vendorsLoading ||
    familiesLoading ||
    subFamiliesLoading ||
    barcodeType1sLoading ||
    barcodeType2sLoading ||
    currenciesLoading ||
    unitsOfMeasureLoading;

  // Combine all lookup errors
  const allLookupsError =
    vendorsError ||
    familiesError ||
    subFamiliesError ||
    barcodeType1sError ||
    barcodeType2sError ||
    currenciesError ||
    unitsOfMeasureError;


  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);

  const handleAddProduct = () => {
    setEditingProduct(null);
    setIsFormOpen(true);
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setIsFormOpen(true);
  };

  const handleDeleteProduct = async (id: number) => {
    await deleteProduct(id);
  };

  // NEW: Consolidated handler for product creation/update
  const handleFormSubmit = async (data: ProductCreate | ProductUpdate, isComposite: boolean): Promise<boolean | number> => {
    setFormSubmitting(true);

    try {
      let result: number | boolean;
      if (editingProduct) {
        // This is an update operation. The switch is hidden for edits, so isComposite will be false.
        const updateData: ProductUpdate = {
          ...data,
          updated_by: 'tester',
        };
        result = await updateProduct(editingProduct.id!, updateData);
      } else {
        // This is a creation operation
        const createData: ProductCreate = {
          ...data as ProductCreate, // Cast to ProductCreate for creation
          created_by: 'tester',
        };

        result = await createProduct(createData);

        if (isComposite && typeof result === 'number') {
          // If it was a composite creation, redirect to composition edit page
          setIsFormOpen(false); // Close form on success
          navigate(`/composition-edit/${result}`);
          return result; // Return the new product ID
        }
      }

      if (result) { // If update was successful or standard product created
        setIsFormOpen(false); // Close form on success
        return result; // Return true for update, or new product ID for standard creation
      } else {
        return false; // Operation failed
      }
    } catch (e: any) {
      // Global error handler will catch this.
      return false; // Indicate failure
    } finally {
      setFormSubmitting(false);
    }
  };


  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingProduct(null);
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-secondary-foreground">Product Management</h2>
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleAddProduct} disabled={allLookupsLoading}>
              {allLookupsLoading ? (
                <span className="flex items-center gap-2">
                  <Spinner size="sm" /> Loading form...
                </span>
              ) : (
                'Add New Product'
              )}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
              <DialogDescription>
                {editingProduct ? 'Make changes to the product here.' : 'Add a new product record.'}
              </DialogDescription>
            </DialogHeader>
            {allLookupsError && (
              <p className="text-destructive text-sm p-2 bg-red-100 border border-red-400 rounded">
                Error loading lookup data: {allLookupsError}
              </p>
            )}
            <ProductForm
              initialData={editingProduct}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={formSubmitting}
              vendors={vendors}
              families={families}
              subFamilies={subFamilies}
              barcodeType1s={barcodeType1s}
              barcodeType2s={barcodeType2s}
              currencies={currencies}
              unitsOfMeasure={unitsOfMeasure}
              lookupLoading={allLookupsLoading}
            />
          </DialogContent>
        </Dialog>
      </div>

      <ProductTable
        products={products}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onEdit={handleEditProduct}
        onDelete={handleDeleteProduct}
        loading={loading}
        error={error}
        lookupLoading={allLookupsLoading}
        baseEndpoint="/products" // NEW: Pass the base endpoint
      />
    </div>
  );
};

export default ProductsPage;